/**
 * Component for a single textfield.
 * This Components exists also as Copy in input.textfieldkeyboard.component.ts. There is a virtual keyboard included
 */

import {Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, SimpleChange, HostListener} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'textfield',
    templateUrl: './input.textfield.component.html'
})
export class TextfieldComponent implements OnInit, OnChanges{
    static DATEFORMAT_GERMAN = 'tt.mm.jjjj';
    static DATEFORMAT_ENGLISH = 'dd.mm.yyyy';
    static DATEFORMAT_FRENCH = 'jj.mm.aaaa';

    /**
     * Text for the label
     */
    @Input() label: string;

    /**
     * Text in case of errors
     */
    @Input() error: string;

    /**
     * name for the field
     */
    @Input() name: string;

    /**
     * initial value for the field
     */
    @Input() value: string;

    /**
     * is this a required field or not
     */
    @Input() required: boolean;

    /**
     * the modelobject for the value
     */
    @Input() model: any;

    /**
     * extra class for the field wrapper
     */
    @Input() wrapperClass: string;

    /**
     * form object for the validation
     */
    @Input() form: FormGroup;

    /**
     * textkey in case we want to show a infotext
     * @type {null}
     */
    @Input() infotTextkey: string = null;

    /**
     * type of the field
     * @type {string}
     */
    @Input() fieldType: string = "text";

    /**
     * maximum length of the input field
     * @type {null}
     */
    @Input() maxLength: number = null;

    /**
     * Label should be always visible or not
     */
    @Input() showLabel: boolean = false;

    /**
     * Should the autocomplete feature be activated or not
     */
    @Input() autocomplete: boolean = true;

    /**
     * @description A variable that represents whether or not the date fieldtype should be forced.
     *
     * @type {boolean}
     */
    @Input() forceDate = false;

    /**
     * A variable that represents whether or not the placeholder should be forced.
     */
    @Input() forcedPlaceholder: string = null;

    /**
     * Should the input be readonly
     */
    @Input() readonly: boolean = false;


    /**
     * eventemitter if the value changes
     * @type {EventEmitter<any>}
     */
    @Output() modelChange: EventEmitter<any> = new EventEmitter();

    /**
     * fires if the textfields is blurred
     */
    @Output() onBlur: EventEmitter<void> = new EventEmitter<void>();

    /**
     * fires if the textfields is focused
     */
    @Output() onFocus: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Eventemitter for clicks on the label.
     */
    @Output() onLabelClick: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Controls if the infotext should be whon or not
     * @type {boolean}
     */
    showInfotext = false;

    /**
     * Does the field has a value or not
     * @type {boolean}
     */
    hasValue: boolean = false;

    /**
     * can be set manually to set a special placeholder that is different from the label
     *
     * @type {String}
     */
    placeholder: string = null;

    /**
     * can be used for validation of special fields
     */
    pattern: string = null;



    /**
     * constructor
     * @param translate
     */
    public constructor(private translate: TranslateService) {}

    /**
     * called if the component is initialized
     */
    public ngOnInit(): void {
        if(this.infotTextkey !== null) {
            this.wrapperClass += ' info';
        }

        if(this.value && this.value.length > 0) {
            this.hasValue = true;
        }

        // implement date field as textfield because it's easier to use
        if(this.fieldType === "date") {
            this.initDateField();
        }
    }

    /**
     * Called if the component is changed
     * @param {SimpleChanges} changes
     */
    ngOnChanges(changes: SimpleChanges) {
        // is triggered every time the model changes (from outside) and updates the css classes
        if(changes.model && changes.model.currentValue) {
            this.hasValue = (changes.model.currentValue.length > 0);
        }

        // if the placeholder is forced, we have to update it
        if(changes.forcedPlaceholder && changes.forcedPlaceholder.currentValue) {
            this.placeholder = changes.forcedPlaceholder.currentValue;
        }
    }

    /**
     * Is called if the user clicks the body
     * @param event
     */
    @HostListener('window:click', ['$event'])
    onClickOutside(event: any): void {
        if(this.showInfotext == true && event.target.localName != "i") {
            this.showInfotext = false;
        }
    }

    /**
     * Returns if the field is valid or not
     * @returns {boolean}
     */
    elementValid(): boolean {
        if(!this.form.controls[this.name]) {
            return false;
        }

        return this.form.controls[this.name].disabled ||
            this.form.controls[this.name].touched == false ||
            this.form.controls[this.name].valid == true;
    }

    /**
     * Function to update the model and inform the parent Component about it.
     * We can not use databinding here because it will not work together with
     * @param event
     */
    updateModel(event:any) {
        this.model = event.target.value;
        this.modelChange.emit(this.model);
        this.hasValue = (this.model.length > 0);
    }

    /**
     * initialize the date field
     */
    initDateField(): void {
        this.fieldType = this.forceDate === true ? "date" : "text";
        // set patter (i.e. 01.01.1990 or 1.1.90)
        this.pattern = this.forceDate === true ? null : '[0-9]{2}\\.[0-9]{2}\\.[0-9]{4}';
        // use a different placeholder, based on the browserlanguage
        switch(this.translate.getBrowserLang()) {
            case "de":
                this.placeholder = ' (' + TextfieldComponent.DATEFORMAT_GERMAN + ')';
                break;
            case "fr":
                this.placeholder = ' (' + TextfieldComponent.DATEFORMAT_FRENCH + ')';
                break;
            case "en":
            case null:
            case "":
            default:
                // in case we have a language but there is no translation available
                this.placeholder = ' (' + TextfieldComponent.DATEFORMAT_ENGLISH + ')';
        }
    }

    /**
     * When the label is clicked.
     */
    labelClick(): void {
        this.onLabelClick.emit();
    }

}
